import type { AssetKind } from 'fym-shared/src/music24/assetKinds';
import type { LocatedAsset } from '../../../../../music24-api/src/kit/posts/locateAsset';
import { SMLRouteNames } from '../../../routing/sml/SMLRoutes';

export type { LocatedAsset };

/**
 * Where an old link of the Assets page or of the Work page goes (design assets-rethink d1, owner
 * decisions 2026-10-09): by the kind of the file. A keeper opens the Kit tab of its release, a post
 * opens the Posts page. Pure, so the rules are tested without React.
 *
 * - `promoteAssets?asset=<uuid>` and `promoteWork?result=<uuid>` (also `reel=`): the file in its
 *   home, open in the viewer. With `state=failed` the home opens with no viewer (a failed file has
 *   none). An archived post opens the archive with the file open.
 * - `promoteAssets?state=working|failed&catalogItemUuid=` and the same Work link: the Kit tab of the
 *   release when only keepers of the song are made now or failed (`kinds`), else Posts of the song.
 * - `promoteWork?catalogItemUuid=&kind=keeper|post` (the main button of a work email, 2026-10-10):
 *   the Kit tab or the Posts tab of the release.
 * - A link with a song and nothing else (the work emails before 2026-10-10, and a uuid that the
 *   artist does not have): the release page of the song.
 * - Every other link: Posts.
 *
 * `accountId` stays, so the artist of the link is selected first. The work emails and the pushes
 * keep their `promoteWork` link on purpose: the native apps open their Work screen from it.
 *
 * Owner, 2026-10-11: Work is a page again. `work/workLinkTarget.ts` decides first: the plain Work
 * route and a link to work that still runs or failed show the Work page; every other Work link
 * comes here, so a link of a finished file lands where it did before. An Assets link always comes here.
 */
export interface FilesRedirectInput {
  search: string;
  /** The file of the uuid of the link; null when the artist has none; undefined with no uuid. */
  located: LocatedAsset | null | undefined;
  /** The kinds of the files of the song that are made now or failed; undefined when not read. */
  busyKinds: ReadonlySet<AssetKind> | undefined;
}

/** The uuid of a file that an old link names: `asset`, else `result`, else `reel`. Empty: none. */
export function linkedFileUuid(search: string): string {
  const params = new URLSearchParams(search);
  return params.get('asset') ?? params.get('result') ?? params.get('reel') ?? '';
}

function withAccount(query: URLSearchParams, params: URLSearchParams): string {
  const accountId = params.get('accountId');
  if (accountId !== null && accountId !== '') {
    query.set('accountId', accountId);
  }
  const text = query.toString();
  return text === '' ? '' : `?${text}`;
}

function kitTab(song: string, asset: string | null, params: URLSearchParams): string {
  const query = new URLSearchParams({ catalogItemUuid: song, tab: 'kit' });
  if (asset !== null) {
    query.set('asset', asset);
  }
  return `/${SMLRouteNames.promote}/${SMLRouteNames.promoteRelease}${withAccount(query, params)}`;
}

function releasePage(song: string, tab: 'posts' | null, params: URLSearchParams): string {
  const query = new URLSearchParams({ catalogItemUuid: song });
  if (tab !== null) {
    query.set('tab', tab);
  }
  return `/${SMLRouteNames.promote}/${SMLRouteNames.promoteRelease}${withAccount(query, params)}`;
}

function postsPage(entries: Record<string, string | null>, params: URLSearchParams): string {
  const query = new URLSearchParams();
  Object.entries(entries).forEach(([key, value]) => {
    if (value !== null && value !== '') {
      query.set(key, value);
    }
  });
  return `/${SMLRouteNames.promote}/${SMLRouteNames.promotePosts}${withAccount(query, params)}`;
}

export function filesRedirectTarget({ search, located, busyKinds }: FilesRedirectInput): string {
  const params = new URLSearchParams(search);
  const song = params.get('catalogItemUuid') ?? '';
  const isFailed = params.get('state') === 'failed';

  if (located !== undefined && located !== null) {
    const fileSong = located.catalogItemUuid ?? song;
    if (located.kind === 'keeper' && fileSong !== '') {
      return kitTab(fileSong, isFailed ? null : located.uuid, params);
    }
    if (located.archived) {
      return postsPage({ view: 'archive', asset: isFailed ? null : located.uuid }, params);
    }
    return postsPage({ catalogItemUuid: fileSong, asset: isFailed ? null : located.uuid }, params);
  }

  if (song === '') {
    return postsPage({}, params);
  }
  if (params.get('state') !== null) {
    if (busyKinds !== undefined && busyKinds.has('keeper') && !busyKinds.has('post')) {
      return kitTab(song, null, params);
    }
    return postsPage({ catalogItemUuid: song }, params);
  }
  const kind = params.get('kind');
  if (kind === 'keeper') {
    return kitTab(song, null, params);
  }
  return releasePage(song, kind === 'post' ? 'posts' : null, params);
}
